/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBooleanDecoder,
  getBooleanEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getOptionDecoder,
  getOptionEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU16Decoder,
  getU16Encoder,
  getU64Decoder,
  getU64Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type Option,
  type OptionOrNullable,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import { getAccountMetaFactory, type ResolvedAccount } from "../shared";

export const UPDATE_PROTOCOL_CONFIG_DISCRIMINATOR = new Uint8Array([
  197, 97, 123, 54, 221, 168, 11, 135,
]);

export function getUpdateProtocolConfigDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    UPDATE_PROTOCOL_CONFIG_DISCRIMINATOR,
  );
}

export type UpdateProtocolConfigInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountConfig extends string | AccountMeta<string> = string,
  TAccountAuthority extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountConfig extends string
        ? WritableAccount<TAccountConfig>
        : TAccountConfig,
      TAccountAuthority extends string
        ? ReadonlySignerAccount<TAccountAuthority> &
            AccountSignerMeta<TAccountAuthority>
        : TAccountAuthority,
      ...TRemainingAccounts,
    ]
  >;

export type UpdateProtocolConfigInstructionData = {
  discriminator: ReadonlyUint8Array;
  escrowFeeBps: Option<number>;
  agentRegistrationFee: Option<bigint>;
  listingFee: Option<bigint>;
  disputeFeeBps: Option<number>;
  feesEnabled: Option<boolean>;
  treasury: Option<Address>;
  buybackPool: Option<Address>;
  moderatorPool: Option<Address>;
};

export type UpdateProtocolConfigInstructionDataArgs = {
  escrowFeeBps: OptionOrNullable<number>;
  agentRegistrationFee: OptionOrNullable<number | bigint>;
  listingFee: OptionOrNullable<number | bigint>;
  disputeFeeBps: OptionOrNullable<number>;
  feesEnabled: OptionOrNullable<boolean>;
  treasury: OptionOrNullable<Address>;
  buybackPool: OptionOrNullable<Address>;
  moderatorPool: OptionOrNullable<Address>;
};

export function getUpdateProtocolConfigInstructionDataEncoder(): Encoder<UpdateProtocolConfigInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["escrowFeeBps", getOptionEncoder(getU16Encoder())],
      ["agentRegistrationFee", getOptionEncoder(getU64Encoder())],
      ["listingFee", getOptionEncoder(getU64Encoder())],
      ["disputeFeeBps", getOptionEncoder(getU16Encoder())],
      ["feesEnabled", getOptionEncoder(getBooleanEncoder())],
      ["treasury", getOptionEncoder(getAddressEncoder())],
      ["buybackPool", getOptionEncoder(getAddressEncoder())],
      ["moderatorPool", getOptionEncoder(getAddressEncoder())],
    ]),
    (value) => ({
      ...value,
      discriminator: UPDATE_PROTOCOL_CONFIG_DISCRIMINATOR,
    }),
  );
}

export function getUpdateProtocolConfigInstructionDataDecoder(): Decoder<UpdateProtocolConfigInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["escrowFeeBps", getOptionDecoder(getU16Decoder())],
    ["agentRegistrationFee", getOptionDecoder(getU64Decoder())],
    ["listingFee", getOptionDecoder(getU64Decoder())],
    ["disputeFeeBps", getOptionDecoder(getU16Decoder())],
    ["feesEnabled", getOptionDecoder(getBooleanDecoder())],
    ["treasury", getOptionDecoder(getAddressDecoder())],
    ["buybackPool", getOptionDecoder(getAddressDecoder())],
    ["moderatorPool", getOptionDecoder(getAddressDecoder())],
  ]);
}

export function getUpdateProtocolConfigInstructionDataCodec(): Codec<
  UpdateProtocolConfigInstructionDataArgs,
  UpdateProtocolConfigInstructionData
> {
  return combineCodec(
    getUpdateProtocolConfigInstructionDataEncoder(),
    getUpdateProtocolConfigInstructionDataDecoder(),
  );
}

export type UpdateProtocolConfigAsyncInput<
  TAccountConfig extends string = string,
  TAccountAuthority extends string = string,
> = {
  config?: Address<TAccountConfig>;
  authority: TransactionSigner<TAccountAuthority>;
  escrowFeeBps: UpdateProtocolConfigInstructionDataArgs["escrowFeeBps"];
  agentRegistrationFee: UpdateProtocolConfigInstructionDataArgs["agentRegistrationFee"];
  listingFee: UpdateProtocolConfigInstructionDataArgs["listingFee"];
  disputeFeeBps: UpdateProtocolConfigInstructionDataArgs["disputeFeeBps"];
  feesEnabled: UpdateProtocolConfigInstructionDataArgs["feesEnabled"];
  treasury: UpdateProtocolConfigInstructionDataArgs["treasury"];
  buybackPool: UpdateProtocolConfigInstructionDataArgs["buybackPool"];
  moderatorPool: UpdateProtocolConfigInstructionDataArgs["moderatorPool"];
};

export async function getUpdateProtocolConfigInstructionAsync<
  TAccountConfig extends string,
  TAccountAuthority extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: UpdateProtocolConfigAsyncInput<TAccountConfig, TAccountAuthority>,
  config?: { programAddress?: TProgramAddress },
): Promise<
  UpdateProtocolConfigInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountAuthority
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    config: { value: input.config ?? null, isWritable: true },
    authority: { value: input.authority ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.config.value) {
    accounts.config.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            112, 114, 111, 116, 111, 99, 111, 108, 95, 99, 111, 110, 102, 105,
            103,
          ]),
        ),
      ],
    });
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.config),
      getAccountMeta(accounts.authority),
    ],
    data: getUpdateProtocolConfigInstructionDataEncoder().encode(
      args as UpdateProtocolConfigInstructionDataArgs,
    ),
    programAddress,
  } as UpdateProtocolConfigInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountAuthority
  >);
}

export type UpdateProtocolConfigInput<
  TAccountConfig extends string = string,
  TAccountAuthority extends string = string,
> = {
  config: Address<TAccountConfig>;
  authority: TransactionSigner<TAccountAuthority>;
  escrowFeeBps: UpdateProtocolConfigInstructionDataArgs["escrowFeeBps"];
  agentRegistrationFee: UpdateProtocolConfigInstructionDataArgs["agentRegistrationFee"];
  listingFee: UpdateProtocolConfigInstructionDataArgs["listingFee"];
  disputeFeeBps: UpdateProtocolConfigInstructionDataArgs["disputeFeeBps"];
  feesEnabled: UpdateProtocolConfigInstructionDataArgs["feesEnabled"];
  treasury: UpdateProtocolConfigInstructionDataArgs["treasury"];
  buybackPool: UpdateProtocolConfigInstructionDataArgs["buybackPool"];
  moderatorPool: UpdateProtocolConfigInstructionDataArgs["moderatorPool"];
};

export function getUpdateProtocolConfigInstruction<
  TAccountConfig extends string,
  TAccountAuthority extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: UpdateProtocolConfigInput<TAccountConfig, TAccountAuthority>,
  config?: { programAddress?: TProgramAddress },
): UpdateProtocolConfigInstruction<
  TProgramAddress,
  TAccountConfig,
  TAccountAuthority
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    config: { value: input.config ?? null, isWritable: true },
    authority: { value: input.authority ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.config),
      getAccountMeta(accounts.authority),
    ],
    data: getUpdateProtocolConfigInstructionDataEncoder().encode(
      args as UpdateProtocolConfigInstructionDataArgs,
    ),
    programAddress,
  } as UpdateProtocolConfigInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountAuthority
  >);
}

export type ParsedUpdateProtocolConfigInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    config: TAccountMetas[0];
    authority: TAccountMetas[1];
  };
  data: UpdateProtocolConfigInstructionData;
};

export function parseUpdateProtocolConfigInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedUpdateProtocolConfigInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 2) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: { config: getNextAccount(), authority: getNextAccount() },
    data: getUpdateProtocolConfigInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
